/**
 * Single source of truth for the homepage's WhatsApp contact CTA (EPIC-38,
 * user decision 2026-09-17: "Log in and contact to whatsapp. I'll tell you
 * the number later.").
 *
 * DO NOT put a plausible-looking placeholder number here. A `wa.me/<digits>`
 * link resolves against a REAL WhatsApp account — an invented number is not
 * inert, it can land a stranger's chat in front of our users, or ours in
 * front of theirs. Leave this empty until the real number is supplied.
 *
 * `Home.tsx` hides the WhatsApp CTA entirely (does not render a disabled or
 * dead button) whenever this is empty, via `buildWhatsAppContactUrl`
 * returning `null`. Nothing else needs to change when the number lands.
 *
 * TO SET THE REAL NUMBER: replace the empty string below with digits only,
 * international format, no leading "+", no spaces/dashes/parentheses (e.g.
 * Mexico City "+52 55 1234 5678" -> "525512345678") — this is wa.me's own
 * required link format. That is the entire change; nothing else in this
 * file or any caller needs to be touched.
 *
 * Real number supplied by the user 2026-09-18: Costa Rica "+506 8348 4756"
 * -> "50683484756" (country code 506 + subscriber 83484756, "+" and spaces
 * stripped per the rule above).
 */
export const WHATSAPP_CONTACT_NUMBER = "50683484756";

/**
 * Builds a `wa.me` deep link with a prefilled, URL-encoded message, or
 * `null` when no number is configured yet — callers must treat `null` as
 * "do not render this CTA," not as an error. `number` is passed in explicitly
 * (rather than this function reading `WHATSAPP_CONTACT_NUMBER` itself) so the
 * empty/populated branches are both directly unit-testable without module
 * mocking — call sites pass `WHATSAPP_CONTACT_NUMBER` from above.
 */
export function buildWhatsAppContactUrl(
  number: string,
  prefilledMessage: string,
): string | null {
  if (!number) return null;
  return `https://wa.me/${number}?text=${encodeURIComponent(prefilledMessage)}`;
}
